import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon,
  SparklesIcon,
  RocketLaunchIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ArrowRightOnRectangleIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  UserIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import OrganizationSwitcher from './OrganizationSwitcher';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarHidden, setDesktopSidebarHidden] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isConnected } = useWebSocket();
  const { user, logout } = useAuth();

  // Build navigation dynamically based on user role
  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: SparklesIcon },
    { name: 'Projects', href: '/app-projects', icon: RocketLaunchIcon },
    { name: 'Problems', href: '/app-problems', icon: ExclamationTriangleIcon },
    { name: 'Hypotheses', href: '/app-hypotheses', icon: LightBulbIcon },
    { name: 'Matrix', href: '/matrix', icon: ChartBarIcon },
    { name: 'Reports', href: '/reports', icon: DocumentTextIcon },
  ];

  // Add admin link if user is admin
  if (user?.role === 'admin') {
    navigation.push({ name: 'Admin', href: '/admin', icon: ShieldCheckIcon });
  }

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleExport = async () => {
    const token = localStorage.getItem('leanai_token');
    if (!token) {
      setError('Authentication required');
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_URL environment variable must be set');
      }
      const response = await fetch(`${API_BASE_URL}/database/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leanai-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setImportStatus('✅ Data exported successfully!');
      setTimeout(() => setImportStatus(''), 3000);
    } catch (err) {
      setError('Failed to export data. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const token = localStorage.getItem('leanai_token');
    if (!token) {
      setError('Authentication required');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_URL environment variable must be set');
      }
      const response = await fetch(`${API_BASE_URL}/database/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to import data');
      }

      const result = await response.json();
      setImportStatus(`✅ Data imported! ${result.projects_count} projects, ${result.problems_count} problems, ${result.hypotheses_count} hypotheses`);
      setTimeout(() => setImportStatus(''), 5000);
    } catch (err) {
      setError('Failed to import data. Please check your file format.');
      setTimeout(() => setError(''), 3000);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Hidden file input for import functionality */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleImport}
        className="hidden"
      />
      {/* Mobile sidebar */}
      <div className={`lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 z-40 flex">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative flex w-64 flex-col bg-indigo-700 pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center px-4">
              <div className="h-12 w-12 mr-3 bg-white rounded-full p-0 flex items-center justify-center">
                <img src="/logo.png" alt="LeanAI" className="h-12 w-12" />
              </div>
              <h1 className="text-2xl font-bold text-white">LeanAI</h1>
              <button
                type="button"
                className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-md p-2 text-indigo-200 hover:bg-indigo-600 hover:text-white focus:outline-none"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <nav className="mt-5 flex-1 space-y-1 px-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium ${
                    location.pathname === item.href
                      ? 'bg-indigo-800 text-white'
                      : 'text-indigo-100 hover:bg-indigo-600 hover:bg-opacity-75'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              ))}
              
              {/* Import/Export buttons for mobile */}
              <div className="pt-4 border-t border-indigo-600">
                <button
                  onClick={handleExport}
                  className="group flex items-center rounded-md px-2 py-2 text-sm font-medium text-indigo-100 hover:bg-indigo-600 hover:bg-opacity-75 w-full"
                >
                  <ArrowDownTrayIcon className="mr-3 h-5 w-5" />
                  Export Data
                </button>
                
                <button 
                  onClick={handleImportClick}
                  className="group flex items-center rounded-md px-2 py-2 text-sm font-medium text-indigo-100 hover:bg-indigo-600 hover:bg-opacity-75 w-full"
                >
                  <ArrowUpTrayIcon className="mr-3 h-5 w-5" />
                  Import Data
                </button>
                
                <button
                  onClick={handleLogout}
                  className="group flex items-center rounded-md px-2 py-2 text-sm font-medium text-indigo-100 hover:bg-indigo-600 hover:bg-opacity-75 w-full"
                >
                  <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                  Logout
                </button>
              </div>
            </nav>
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${
        desktopSidebarHidden ? 'lg:w-16' : 'lg:w-64'
      }`}>
        <div className="flex min-h-0 flex-1 flex-col bg-indigo-700">
          <div className={`flex h-16 flex-shrink-0 items-center justify-between ${
            desktopSidebarHidden ? 'px-2' : 'px-4'
          }`}>
            <div className="flex items-center">
              <div className="h-12 w-12 bg-white rounded-full p-0 flex items-center justify-center">
                <img src="/logo.png" alt="LeanAI" className="h-12 w-12" />
              </div>
              {!desktopSidebarHidden && (
                <h1 className="ml-3 text-2xl font-bold text-white">LeanAI</h1>
              )}
            </div>
            <button
              onClick={() => setDesktopSidebarHidden(!desktopSidebarHidden)}
              className="p-2 rounded-md text-indigo-200 hover:text-white hover:bg-indigo-600 transition-colors"
              title={desktopSidebarHidden ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {desktopSidebarHidden ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              )}
            </button>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto">
            <nav className="flex-1 space-y-1 px-2 py-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium ${
                    location.pathname === item.href
                      ? 'bg-indigo-800 text-white'
                      : 'text-indigo-100 hover:bg-indigo-600 hover:bg-opacity-75'
                  } ${desktopSidebarHidden ? 'justify-center' : ''}`}
                  title={desktopSidebarHidden ? item.name : ''}
                >
                  <item.icon className={`h-5 w-5 ${desktopSidebarHidden ? '' : 'mr-3'}`} />
                  {!desktopSidebarHidden && item.name}
                </Link>
              ))}
              
              {/* Import/Export buttons for desktop */}
              <div className="pt-4 border-t border-indigo-600 space-y-1">
                <button
                  onClick={handleExport}
                  className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium text-indigo-100 hover:bg-indigo-600 hover:bg-opacity-75 w-full ${
                    desktopSidebarHidden ? 'justify-center' : ''
                  }`}
                  title={desktopSidebarHidden ? 'Export Data' : ''}
                >
                  <ArrowDownTrayIcon className={`h-5 w-5 ${desktopSidebarHidden ? '' : 'mr-3'}`} />
                  {!desktopSidebarHidden && 'Export Data'}
                </button>
                
                <button 
                  onClick={handleImportClick}
                  className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium text-indigo-100 hover:bg-indigo-600 hover:bg-opacity-75 w-full ${
                    desktopSidebarHidden ? 'justify-center' : ''
                  }`}
                  title={desktopSidebarHidden ? 'Import Data' : ''}
                >
                  <ArrowUpTrayIcon className={`h-5 w-5 ${desktopSidebarHidden ? '' : 'mr-3'}`} />
                  {!desktopSidebarHidden && 'Import Data'}
                </button>
                
                <button
                  onClick={handleLogout}
                  className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium text-indigo-100 hover:bg-indigo-600 hover:bg-opacity-75 w-full ${
                    desktopSidebarHidden ? 'justify-center' : ''
                  }`}
                  title={desktopSidebarHidden ? 'Logout' : ''}
                >
                  <ArrowRightOnRectangleIcon className={`h-5 w-5 ${desktopSidebarHidden ? '' : 'mr-3'}`} />
                  {!desktopSidebarHidden && 'Logout'}
                </button>
              </div>
            </nav>
          </div>
          <div className="flex flex-shrink-0 p-4">
            <div className="w-full space-y-2">
              {/* Organization Switcher */}
              {!desktopSidebarHidden && user && user.organizations && (
                <OrganizationSwitcher
                  organizations={user.organizations}
                  currentOrgId={user.organization_id}
                  currentOrgName={user.organization_name}
                />
              )}
              
              {/* User Info */}
              {!desktopSidebarHidden && user && (
                <div className="w-full rounded-md bg-indigo-800 p-3 text-xs text-indigo-100">
                  <div className="flex items-center mb-2">
                    <UserIcon className="h-4 w-4 mr-2" />
                    <span className="font-medium truncate">{user.name}</span>
                  </div>
                  <div className="text-indigo-300 text-xs">
                    Role: <span className="capitalize">{user.role}</span>
                  </div>
                </div>
              )}
              
              {/* Connection Status */}
              <div className={`flex w-full items-center rounded-md bg-indigo-800 p-2 text-sm text-indigo-200 ${
                desktopSidebarHidden ? 'justify-center' : ''
              }`}>
                <div
                  className={`h-2 w-2 rounded-full ${
                    isConnected ? 'bg-green-400' : 'bg-red-400'
                  } ${desktopSidebarHidden ? '' : 'mr-2'}`}
                  title={desktopSidebarHidden ? (isConnected ? 'Connected' : 'Disconnected') : ''}
                />
                {!desktopSidebarHidden && (isConnected ? 'Connected' : 'Disconnected')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`flex flex-1 flex-col transition-all duration-300 ${
        desktopSidebarHidden ? 'lg:pl-16' : 'lg:pl-64'
      }`}>
        <div className="sticky top-0 z-10 bg-white shadow-sm lg:hidden">
          <div className="flex h-16 items-center justify-between px-4">
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 focus:outline-none"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex items-center">
              <div className="h-10 w-10 mr-2 bg-white rounded-full p-0 flex items-center justify-center shadow-sm">
                <img src="/logo.png" alt="LeanAI" className="h-10 w-10" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                {navigation.find((item) => item.href === location.pathname)?.name || 'LeanAI'}
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleLogout}
                className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50"
                title="Logout"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        <main className="flex-1">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            {/* Status messages */}
            {importStatus && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                {importStatus}
              </div>
            )}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                {error}
              </div>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
